/**
 * Спільний протокол клієнт <-> сервер.
 *
 * Версіонується: клієнт надсилає `v` у `hello`; сервер відхиляє несумісні версії.
 * Новий фронтенд (інший дизайн, мобільний застосунок) підключається до того самого
 * сервера, реалізувавши лише ці повідомлення.
 */
import { z } from 'zod';

export const PROTOCOL_VERSION = 1;

/** Ліміти, спільні для клієнта й сервера. */
export const LIMITS = {
  /** Максимальний розмір звичайного повідомлення, байт. */
  MESSAGE_BYTES: 16 * 1024,
  /** Максимальний розмір base64-кадру у скарзі, символів. */
  REPORT_FRAME_CHARS: 300_000,
  /** Максимальна довжина причини скарги. */
  REPORT_REASON_CHARS: 200,
} as const;

// ---------------------------------------------------------------- фільтри
/**
 * Фільтри підбору. У прототипі сервер їх приймає, але ігнорує
 * (FILTERS_ENABLED=false). Платні фільтри вмикаються конфігом, без змін протоколу.
 */
export const Filters = z
  .object({
    country: z.string().length(2).toUpperCase().optional(),
    gender: z.enum(['any', 'male', 'female']).optional(),
    interests: z.array(z.string().min(1).max(32)).max(10).optional(),
  })
  .strict();
export type Filters = z.infer<typeof Filters>;

// ---------------------------------------------------------------- клієнт -> сервер
export const SignalKind = z.enum(['offer', 'answer', 'candidate']);
export type SignalKind = z.infer<typeof SignalKind>;

export const Route = z.enum(['direct', 'relay', 'unknown']);
export type Route = z.infer<typeof Route>;

// ---------------------------------------------------------------- скарги
/**
 * Фіксований список причин (замість вільного тексту): сервер має надійно
 * розрізняти critical-причини від звичайних, а не парсити довільний рядок.
 */
export const ReportReason = z.enum(['nudity', 'harassment', 'minor', 'spam', 'other']);
export type ReportReason = z.infer<typeof ReportReason>;

/**
 * Причини, що йдуть окремим пріоритетним шляхом (Wave 1, п. 2) замість
 * звичайного порогу "N скаржників за 24г". Список свідомо мінімальний і
 * розширюється лише product-рішенням, не автоматично.
 */
export const CRITICAL_REPORT_REASONS: readonly ReportReason[] = ['minor'];
export function isCriticalReason(reason: ReportReason): boolean {
  return CRITICAL_REPORT_REASONS.includes(reason);
}

export const ClientMsg = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hello'),
    v: z.number().int(),
    /** Підписаний ідентифікатор сесії, виданий сервером раніше. */
    sessionToken: z.string().max(200).optional(),
    ageConfirmed: z.literal(true),
    lang: z.string().max(10).optional(),
  }),
  z.object({ type: z.literal('join'), filters: Filters.optional() }),
  z.object({ type: z.literal('next') }),
  z.object({ type: z.literal('leave') }),
  z.object({
    type: z.literal('signal'),
    pairId: z.string().max(64),
    kind: SignalKind,
    data: z.unknown(),
  }),
  /** Телеметрія для аналітики: маршрут з'єднання, збої, метрики getStats. */
  z.object({
    type: z.literal('telemetry'),
    pairId: z.string().max(64),
    kind: z.enum(['connected', 'failed', 'stats']),
    route: Route.optional(),
    data: z.record(z.union([z.string().max(64), z.number(), z.boolean()])).optional(),
  }),
  z.object({
    type: z.literal('report'),
    pairId: z.string().max(64),
    reason: ReportReason,
    /** JPEG у base64 без префікса data:. */
    frameJpeg: z.string().max(LIMITS.REPORT_FRAME_CHARS).optional(),
  }),
  /**
   * Block — незалежний від Report. Партнер, на якого натиснули Block, більше
   * ніколи (у межах retention) не з'єднується з цією сесією знову.
   */
  z.object({ type: z.literal('block'), pairId: z.string().max(64) }),
]);
export type ClientMsg = z.infer<typeof ClientMsg>;

// ---------------------------------------------------------------- сервер -> клієнт
export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export type ErrorCode =
  | 'bad_message'
  | 'bad_version'
  | 'rate_limited'
  | 'banned'
  /** Тимчасове обмеження після critical-скарги, до рішення модератора. Не бан. */
  | 'restricted'
  | 'not_ready'
  | 'internal';

export type ServerMsg =
  | {
      type: 'hello_ack';
      /** Підписаний токен: клієнт зберігає його й надсилає у наступному `hello`. */
      sessionToken: string;
      iceServers: IceServer[];
      /** `relay` = увесь відеотрафік лише через TURN. */
      iceTransportPolicy: 'all' | 'relay';
      /** Максимальний відеобітрейт, біт/с. */
      maxVideoBitrate: number;
      online: number;
    }
  | { type: 'queued'; waiting: number }
  | { type: 'matched'; pairId: string; role: 'caller' | 'callee' }
  | { type: 'signal'; pairId: string; kind: SignalKind; data: unknown }
  | {
      type: 'peer_left';
      pairId: string;
      /** 'restricted' — партнера тимчасово обмежено після critical-скарги (не бан). */
      reason: 'next' | 'leave' | 'disconnect' | 'timeout' | 'banned' | 'restricted';
    }
  | { type: 'error'; code: ErrorCode; message: string };
